/**
 * React Query hooks for saved analysis views.
 *
 * Provides list, create, update, delete operations with cache management.
 * Also exports helper functions for view name generation and config comparison.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTank } from '@/contexts/TankContext';
import { analysisViewsApi } from '@/api/analysisViews';
import { CACHE_TIMES } from '@/config/cacheTimes';
import type {
  AnalysisViewCreate,
  AnalysisViewUpdate,
  ViewConfig,
} from '@/types/analysisView';

// ---------------------------------------------------------------------------
// Pure helpers (exported for testing)
// ---------------------------------------------------------------------------

/** Compare two ViewConfigs ignoring array order. */
export function configsMatch(a: ViewConfig, b: ViewConfig): boolean {
  const sortedEqual = (x: string[], y: string[]) =>
    x.length === y.length && [...x].sort().join(',') === [...y].sort().join(',');

  return (
    sortedEqual(a.parameters, b.parameters) &&
    sortedEqual(a.dosing, b.dosing) &&
    sortedEqual(a.events, b.events) &&
    a.viewMode === b.viewMode &&
    a.normalized === b.normalized &&
    a.eventLines === b.eventLines &&
    a.smoothing === b.smoothing
  );
}

/** Format a composite key like "alkalinity:aquawiz" into a display name. */
function formatParamName(key: string): string {
  const param = key.split(':')[0] || key;
  return param.charAt(0).toUpperCase() + param.slice(1);
}

/** Generate a human-readable name from a ViewConfig. */
export function generateViewName(config: ViewConfig): string {
  const parts: string[] = [];

  // Parameters: show unique param names (not sources)
  if (config.parameters.length > 0) {
    const uniqueParams = [...new Set(config.parameters.map(formatParamName))];
    if (uniqueParams.length <= 2) {
      parts.push(uniqueParams.join(' + '));
    } else {
      parts.push(`${uniqueParams[0]} + ${uniqueParams.length - 1} more`);
    }
  }

  // Dosing
  if (config.dosing.length > 0) {
    if (config.dosing.length === 1) {
      parts.push(config.dosing[0] ?? 'Dosing');
    } else {
      parts.push(`${config.dosing.length} dosing`);
    }
  }

  // Events
  if (config.events.length > 0) {
    parts.push(`${config.events.length} event${config.events.length > 1 ? 's' : ''}`);
  }

  // View mode suffix
  const suffix = config.viewMode === 'combined'
    ? (config.normalized ? 'Normalized' : 'Combined')
    : 'Stacked';

  const smoothingSuffix = config.smoothing !== 'off' ? ` · ${config.smoothing} avg` : '';
  const base = parts.length > 0
    ? `${parts.join(', ')} — ${suffix}${smoothingSuffix}`
    : `${suffix} View${smoothingSuffix}`;

  return base.length > 50 ? base.slice(0, 47) + '...' : base;
}

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

const viewKeys = {
  all: (tankId: string) => ['analysis-views', tankId] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Fetch all saved views for the active tank. */
export function useAnalysisViews() {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useQuery({
    queryKey: viewKeys.all(tankId || ''),
    queryFn: () => analysisViewsApi.list(tankId!),
    enabled: !!tankId,
    staleTime: CACHE_TIMES.DATA,
    select: (data) => data.views,
  });
}

/** Create a new saved view. */
export function useCreateAnalysisView() {
  const queryClient = useQueryClient();
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useMutation({
    mutationFn: (data: AnalysisViewCreate) =>
      analysisViewsApi.create(tankId!, data),
    onSuccess: () => {
      if (tankId) queryClient.invalidateQueries({ queryKey: viewKeys.all(tankId) });
    },
  });
}

/** Update an existing saved view (name and/or config). */
export function useUpdateAnalysisView() {
  const queryClient = useQueryClient();
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useMutation({
    mutationFn: ({ viewId, data }: { viewId: string; data: AnalysisViewUpdate }) =>
      analysisViewsApi.update(tankId!, viewId, data),
    onSuccess: () => {
      if (tankId) queryClient.invalidateQueries({ queryKey: viewKeys.all(tankId) });
    },
  });
}

/** Delete a saved view. */
export function useDeleteAnalysisView() {
  const queryClient = useQueryClient();
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useMutation({
    mutationFn: (viewId: string) => analysisViewsApi.remove(tankId!, viewId),
    onSuccess: () => {
      if (tankId) queryClient.invalidateQueries({ queryKey: viewKeys.all(tankId) });
    },
  });
}
